import React, { useState, useEffect, useContext, useMemo } from 'react';
import styles from './MessageTemplates.module.css';
import { MdOutlineRadioButtonChecked, MdOutlineRadioButtonUnchecked } from 'react-icons/md';
import { AiOutlineClose } from 'react-icons/ai';
import TemplatePreview from './TemplatePreview';
import { BASE_URL_API } from '../../../References/URLs';
import AuthContext from '../../../store/auth-context';
import ActiveStoreContext from '../../../store/ActiveStoreContext';
import { FaSearch, FaSync } from 'react-icons/fa';

const MessageTemplates = ({ data, getCampaignDetails, setData }) => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = (() => { try { return asCtx?.activeStore && asCtx.activeStore.trim() !== "" ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState({
        category: '',
        language: '',
        deliverySource: '',
        status: '',
        search: '',
    });
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

    useEffect(() => {
        getAllTemplates()
    }, []);

    useEffect(() => {
        // If selectedTemplate is set, update the message preview
        if (selectedTemplate) {
            const updatedMessage = handleVariableChange(selectedTemplate, selectedTemplate.variables.reduce((acc, v) => {
                acc[v.name] = v.value || v.defaultValue;
                return acc;
            }, {}));
            setData({ ...data, messageTemplate: selectedTemplate, message: updatedMessage });
        }
    }, [selectedTemplate]);

    const formatLabel = (value) => {
        if (!value) return '';
        return value
            .toString()
            .replace(/[_-]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const deliverySourceLabel = (value) => {
        if (!value) return '';
        if (value.toUpperCase() === 'META') {
            return 'WhatsApp';
        }
        return formatLabel(value);
    };

    const normalize = (value) => (value ?? '').toString().toLowerCase().trim();

    const getValueByPath = (target, path) => {
        if (!path) return undefined;
        return path.split('.').reduce((acc, segment) => (
            acc && acc[segment] !== undefined ? acc[segment] : undefined
        ), target);
    };

    const filterOptions = useMemo(() => {
        const categories = new Map();
        const languages = new Map();
        const deliverySources = new Map();
        const statuses = new Map();

        templates.forEach((template) => {
            if (template?.category) {
                categories.set(template.category, formatLabel(template.category));
            }
            if (template?.language) {
                languages.set(template.language, formatLabel(template.language));
            }
            if (template?.deliverySource) {
                deliverySources.set(template.deliverySource, deliverySourceLabel(template.deliverySource));
            }
            if (template?.status) {
                statuses.set(template.status, formatLabel(template.status));
            }
        });

        const toSortedArray = (map) => Array.from(map.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label));

        return {
            categories: toSortedArray(categories),
            languages: toSortedArray(languages),
            deliverySources: toSortedArray(deliverySources),
            statuses: toSortedArray(statuses),
        };
    }, [templates]);

    const filteredTemplates = useMemo(() => {
        if (!Array.isArray(templates) || templates.length === 0) {
            return [];
        }

        const searchTerm = normalize(filters.search);
        const categoryFilter = normalize(filters.category);
        const languageFilter = normalize(filters.language);
        const sourceFilter = normalize(filters.deliverySource);
        const statusFilter = normalize(filters.status);

        let data = [...templates];

        data = data.filter((template) => {
            const matchesCategory = !categoryFilter || normalize(template?.category).includes(categoryFilter);
            const matchesLanguage = !languageFilter || normalize(template?.language) === languageFilter;
            const matchesSource = !sourceFilter || normalize(template?.deliverySource) === sourceFilter;
            const matchesStatus = !statusFilter || normalize(template?.status) === statusFilter;

            let matchesSearch = true;
            if (searchTerm) {
                matchesSearch = [
                    template?.templateNamePlatform,
                    template?.templateName,
                    template?.name,
                    template?.templateIdentifier,
                    template?.category,
                    template?.status,
                    template?.language,
                ].some((field) => normalize(field).includes(searchTerm));
            }

            return matchesCategory && matchesLanguage && matchesSource && matchesStatus && matchesSearch;
        });

        if (sortConfig.key) {
            const direction = sortConfig.direction === 'desc' ? -1 : 1;
            data.sort((a, b) => {
                const valueA = getValueByPath(a, sortConfig.key) ?? '';
                const valueB = getValueByPath(b, sortConfig.key) ?? '';

                if (typeof valueA === 'number' && typeof valueB === 'number') {
                    return (valueA - valueB) * direction;
                }

                return normalize(valueA).localeCompare(normalize(valueB)) * direction;
            });
        } else {
            data.sort((a, b) => {
                const dateA = Date.parse(a?.lastEdited?.date || a?.updatedOn || a?.createdOn || 0);
                const dateB = Date.parse(b?.lastEdited?.date || b?.updatedOn || b?.createdOn || 0);
                return dateB - dateA;
            });
        }

        return data;
    }, [templates, filters, sortConfig]);

    const totalTemplatesCount = templates.length;
    const filteredTemplatesCount = filteredTemplates.length;

    const totalPages = useMemo(() => (
        Math.max(1, Math.ceil((filteredTemplatesCount || 0) / rowsPerPage))
    ), [filteredTemplatesCount, rowsPerPage]);

    const paginatedTemplates = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return filteredTemplates.slice(start, start + rowsPerPage);
    }, [filteredTemplates, currentPage, rowsPerPage]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const hasActiveFilters = useMemo(() => (
        Boolean(
            filters.category
            || filters.language
            || filters.deliverySource
            || filters.status
            || (filters.search && filters.search.trim() !== '')
        )
    ), [filters]);

    const displayTotalPages = filteredTemplatesCount === 0 ? 0 : totalPages;
    const displayCurrentPage = filteredTemplatesCount === 0 ? 0 : currentPage;

    const handleSort = (key) => {
        setSortConfig((prev) => {
            const direction = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
            return { key, direction };
        });
    };

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };


    const handleVariableChange = (template, variableValues) => {
        if (!template || !template.content) return '';
        return template.content.replace(/{(\w+)}/g, (_, key) => variableValues[key] || `{${key}}`);
    };


    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const handlePageChange = (direction) => {
        if (direction === 'prev' && currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        } else if (direction === 'next' && currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const resetFilters = () => {
        setFilters({
            category: '',
            language: '',
            deliverySource: '',
            status: '',
            search: '',
        });
        setSortConfig({ key: null, direction: null });
        setCurrentPage(1);
    };

    const getAllTemplates = async () => {
        setLoading(true);
        try {
            const getAllTemplatesResponse = await fetch(BASE_URL_API + "getAllTemplates",
                {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                    })
                });

            if (!getAllTemplatesResponse.ok) {
                throw new Error('Server error while fetching templates');
            }

            const getAllTemplatesRespo = await getAllTemplatesResponse.json();
            if (getAllTemplatesRespo.status === "success") {
                setTemplates(getAllTemplatesRespo?.response || []);
            } else if (getAllTemplatesRespo.message === "tokenExpired") {
                authCtx.logout();
            } else {
                setTemplates([]);
            }
        } catch (error) {
            console.error(error);
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.mainContainer}>
                <div className={styles.toolbar}>
                    <div className={styles.toolbarRow}>
                        <div className={styles.toolbarSummary}>
                            <div>
                                <div className={styles.toolbarTitle}>Message Templates</div>
                                <div className={styles.toolbarSubtitle}>Review, filter, and preview CRM-ready templates.</div>
                            </div>
                            <span className={styles.countBadge}>
                                {filteredTemplatesCount} / {totalTemplatesCount} templates
                            </span>
                        </div>
                        <div className={styles.toolbarActions}>
                            <button
                                type="button"
                                className={styles.refreshBtn}
                                onClick={getAllTemplates}
                                disabled={loading}
                            >
                                <FaSync className={`${styles.refreshIcon} ${loading ? styles.spin : ''}`} />
                                Refresh
                            </button>
                        </div>
                    </div>
                    <div className={styles.toolbarRow}>
                        <div className={styles.searchWrapper}>
                            <div className={styles.searchBar}>
                                <FaSearch className={styles.icon} />
                                <input
                                    type="text"
                                    placeholder="Search templates by name, category, or identifier..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                />
                                {filters.search && (
                                    <button
                                        type="button"
                                        className={styles.clearSearch}
                                        onClick={() => handleFilterChange('search', '')}
                                        aria-label="Clear search"
                                    >
                                        <AiOutlineClose size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className={styles.filterGroup}>
                            <select
                                className={styles.filterSelect}
                                value={filters.category}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                            >
                                <option value="">All categories</option>
                                {filterOptions.categories.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <select
                                className={styles.filterSelect}
                                value={filters.language}
                                onChange={(e) => handleFilterChange('language', e.target.value)}
                            >
                                <option value="">All languages</option>
                                {filterOptions.languages.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <select
                                className={styles.filterSelect}
                                value={filters.deliverySource}
                                onChange={(e) => handleFilterChange('deliverySource', e.target.value)}
                            >
                                <option value="">All sources</option>
                                {filterOptions.deliverySources.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <select
                                className={styles.filterSelect}
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="">All statuses</option>
                                {filterOptions.statuses.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className={styles.resetBtn}
                                onClick={resetFilters}
                                disabled={!hasActiveFilters && sortConfig.key === null}
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
                <div className={styles.bodyContent}>
                    <div className={styles.table}>
                        <div className={styles.header}>
                            <div className={styles.cell}>#</div>
                            <div className={styles.templateName} onClick={() => handleSort('name')}>Template Name</div>
                            <div className={styles.delSource} onClick={() => handleSort('deliverySource')}>Del. Source</div>
                            <div className={styles.cell}>Language</div>
                            <div className={styles.cell}>Status</div>
                            <div className={styles.cell} onClick={() => handleSort('lastEdited.date')}>Last Edited</div>
                            <div className={styles.cell}>Preview</div>
                        </div>
                        <div className={styles.body}>
                            {loading ? (
                                <div className={styles.loaderRow}>
                                    <FaSync className={`${styles.loaderIcon} ${styles.spin}`} />
                                    <span>Loading templates...</span>
                                </div>
                            ) : paginatedTemplates.length === 0 ? (
                                <div className={styles.emptyState}>No templates match the current filters.</div>
                            ) : (
                                paginatedTemplates.map((template, index) => {
                                    const deliveryLabel = deliverySourceLabel(template?.deliverySource) || '—';
                                    const deliveryTagClass = `${styles.deliveryTag} ${deliveryLabel === 'WhatsApp' ? styles.deliveryTagWhatsApp : ''}`;

                                    return (
                                        <div key={template?.templateIdentifier || index} className={styles.row}
                                            onClick={() => {
                                                setPreviewTemplate(template);
                                                setPreviewVisible(true);
                                            }}
                                        >
                                            <div className={styles.cell}>
                                                {data?.templateIdentifier === template.templateIdentifier ? (
                                                    <MdOutlineRadioButtonChecked />
                                                ) : (
                                                    <MdOutlineRadioButtonUnchecked />
                                                )}
                                            </div>
                                            <div className={styles.templateName}>
                                                {template.templateNamePlatform || template.templateName || 'Untitled Template'}
                                            </div>
                                            <div className={styles.delSource}>
                                                <div className={styles.whatsAppWrapper}>
                                                    <div className={deliveryTagClass}>
                                                        {deliveryLabel}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={styles.cell}>{template.language || '—'}</div>
                                            <div className={styles.cell}>{template.status || '—'}</div>
                                            <div className={styles.cell}>{template.lastEdited?.date || template.updatedOn || template.createdOn || '—'}</div>
                                            <div className={styles.cell}>
                                                <button
                                                    className={styles.previewBtn}
                                                    onClick={() => {
                                                        setPreviewTemplate(template);
                                                        setPreviewVisible(true);
                                                    }}
                                                >
                                                    Preview
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                    <div className={styles.tableFooter}>
                        <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                            <option value={5}>5 Rows</option>
                            <option value={10}>10 Rows</option>
                            <option value={20}>20 Rows</option>
                            <option value={50}>50 Rows</option>
                        </select>
                        <div className={styles.pagination}>
                            <button onClick={() => handlePageChange('prev')} disabled={currentPage === 1 || filteredTemplatesCount === 0}>
                                Previous
                            </button>
                            <span>
                                Page {displayCurrentPage} of {displayTotalPages}
                            </span>
                            <button
                                onClick={() => handlePageChange('next')}
                                disabled={currentPage >= totalPages || filteredTemplatesCount === 0}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                    {previewVisible && (
                        <TemplatePreview
                            visible={previewVisible}
                            onClose={() => setPreviewVisible(false)}
                            template={previewTemplate}
                            onVariableChange={handleVariableChange}
                            onTemplateSelect={getCampaignDetails}
                            data={data}
                            setData={setData}
                        />
                    )}

                </div>
            </div>
        </div>
    );
};

export default MessageTemplates;
